import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Save, PlugZap, Loader2, ArrowLeft, RefreshCw, FlaskConical } from 'lucide-react';
import api from './api';

const FieldLabel = ({ children }) => (
  <label className="text-xs font-bold text-slate-600 block mb-1.5">{children}</label>
);

const TextInput = (props) => (
  <input
    {...props}
    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
  />
);

const SettingsPage = ({ onBack }) => {
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({
    sap_api_key: '', sap_base_url: '', aps_client_id: '', aps_client_secret: '',
    default_cost_center: '', default_wbs_element: '', default_plant: '',
  });
  // 🎯 Kept separate from `form` on purpose -- `form` only sends fields the
  // user actually typed something into, but this boolean toggle must always
  // be sent (even when false) so turning it back off actually takes effect.
  const [simulationMode, setSimulationMode] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const [costCenters, setCostCenters] = useState({ available: false, items: [], message: '', loading: false });
  const [projects, setProjects] = useState({ available: false, items: [], message: '', loading: false });

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/v1/settings');
      setCurrent(res.data);
      setForm((f) => ({
        ...f,
        sap_base_url: res.data.sap_base_url || '',
        default_cost_center: res.data.default_cost_center || '',
        default_wbs_element: res.data.default_wbs_element || '',
        default_plant: res.data.default_plant || '',
      }));
      setSimulationMode(!!res.data.simulation_mode);
    } catch (err) {
      toast.error('Failed to load current settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const loadCostCenters = async () => {
    setCostCenters((c) => ({ ...c, loading: true }));
    try {
      const res = await api.get('/v1/sap/cost-centers');
      setCostCenters({ ...res.data, loading: false });
      if (!res.data.available) toast(res.data.message, { icon: 'ℹ️' });
    } catch (err) {
      setCostCenters({ available: false, items: [], message: 'Connection failed', loading: false });
    }
  };

  const loadProjects = async () => {
    setProjects((p) => ({ ...p, loading: true }));
    try {
      const res = await api.get('/v1/sap/projects');
      setProjects({ ...res.data, loading: false });
      if (!res.data.available) toast(res.data.message, { icon: 'ℹ️' });
    } catch (err) {
      setProjects({ available: false, items: [], message: 'Connection failed', loading: false });
    }
  };

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      // We only send fields the user actually typed something into -- so we
      // don't wipe an already-saved key just because the field was left
      // empty in the form.
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== '') payload[k] = v;
      });
      // simulation_mode is a boolean and must always be sent (even when
      // false), unlike the text fields above which we skip when empty
      payload.simulation_mode = simulationMode;
      await api.put('/v1/settings', payload);
      toast.success('Settings saved successfully');
      setForm((f) => ({ ...f, sap_api_key: '', aps_client_secret: '' })); // clear sensitive fields from the form after saving
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const res = await api.post('/v1/settings/test-connection');
      if (res.data.connected) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message, { duration: 6000 });
      }
    } catch (err) {
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6" dir="ltr">
      <div className="max-w-3xl mx-auto">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-xs font-bold mb-4">
          <ArrowLeft size={14} /> Back to Viewer
        </button>

        <h1 className="text-xl font-black text-slate-800 mb-1">General Settings</h1>
        <p className="text-xs text-slate-500 mb-6">Connection keys for SAP and Autodesk, and default values for material requisitions</p>

        {/* Connection keys */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-4">
          <h2 className="text-sm font-black text-slate-800 mb-4">SAP Connection Keys</h2>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <FieldLabel>SAP API Key {current?.sap_api_key_set && <span className="text-emerald-600 font-normal">(currently set: {current.sap_api_key_masked})</span>}</FieldLabel>
              <TextInput type="password" placeholder="Leave blank if you don't want to change it" value={form.sap_api_key} onChange={handleChange('sap_api_key')} />
            </div>
            <div>
              <FieldLabel>SAP Base URL (Purchase Requisition service)</FieldLabel>
              <TextInput type="text" value={form.sap_base_url} onChange={handleChange('sap_base_url')} />
            </div>
          </div>
          <button
            onClick={handleTestConnection}
            disabled={testing}
            className="mt-4 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60"
          >
            {testing ? <Loader2 size={14} className="animate-spin" /> : <PlugZap size={14} />}
            Test SAP Connection
          </button>
        </div>

        {/* Autodesk keys */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-4">
          <h2 className="text-sm font-black text-slate-800 mb-4">Autodesk APS Connection Keys</h2>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <FieldLabel>APS Client ID {current?.aps_client_id_set && <span className="text-emerald-600 font-normal">(currently set: {current.aps_client_id_masked})</span>}</FieldLabel>
              <TextInput type="password" placeholder="Leave blank if you don't want to change it" value={form.aps_client_id} onChange={handleChange('aps_client_id')} />
            </div>
            <div>
              <FieldLabel>APS Client Secret {current?.aps_client_secret_set && <span className="text-emerald-600 font-normal">(set)</span>}</FieldLabel>
              <TextInput type="password" placeholder="Leave blank if you don't want to change it" value={form.aps_client_secret} onChange={handleChange('aps_client_secret')} />
            </div>
          </div>
        </div>

        {/* Default values for material requisitions */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-4">
          <h2 className="text-sm font-black text-slate-800 mb-1">Default Values for Material Requisitions</h2>
          <p className="text-[11px] text-slate-400 mb-4">Automatically applied to any new material request (WBS Element takes priority over Cost Center if both are set)</p>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-1.5">
              <FieldLabel>Default Cost Center</FieldLabel>
              <button onClick={loadCostCenters} className="text-blue-600 text-[11px] font-bold flex items-center gap-1 hover:underline">
                {costCenters.loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Fetch from SAP
              </button>
            </div>
            {costCenters.available && costCenters.items.length > 0 ? (
              <select value={form.default_cost_center} onChange={handleChange('default_cost_center')} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                <option value="">-- Select a Cost Center --</option>
                {costCenters.items.map((c) => (
                  <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                ))}
              </select>
            ) : (
              <>
                <TextInput type="text" placeholder="Enter the Cost Center code manually (e.g.: 1010-CC-01)" value={form.default_cost_center} onChange={handleChange('default_cost_center')} />
                {costCenters.message && <p className="text-[11px] text-amber-600 mt-1">{costCenters.message}</p>}
              </>
            )}
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-1.5">
              <FieldLabel>Default WBS Element (Project)</FieldLabel>
              <button onClick={loadProjects} className="text-blue-600 text-[11px] font-bold flex items-center gap-1 hover:underline">
                {projects.loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Fetch from SAP
              </button>
            </div>
            {projects.available && projects.items.length > 0 ? (
              <select value={form.default_wbs_element} onChange={handleChange('default_wbs_element')} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                <option value="">-- Select a Project --</option>
                {projects.items.map((p) => (
                  <option key={p.code} value={p.code}>{p.code} — {p.name}</option>
                ))}
              </select>
            ) : (
              <>
                <TextInput type="text" placeholder="Enter the WBS Element code manually if known" value={form.default_wbs_element} onChange={handleChange('default_wbs_element')} />
                {projects.message && <p className="text-[11px] text-amber-600 mt-1">{projects.message}</p>}
              </>
            )}
          </div>

          <div>
            <FieldLabel>Default Plant</FieldLabel>
            <TextInput type="text" value={form.default_plant} onChange={handleChange('default_plant')} />
          </div>
        </div>

        {/* Simulation mode */}
        <div className={`rounded-2xl border shadow-sm p-6 mb-4 ${simulationMode ? 'bg-amber-50 border-amber-300' : 'bg-white border-slate-200'}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <FlaskConical size={18} className={simulationMode ? 'text-amber-600 mt-0.5' : 'text-slate-400 mt-0.5'} />
              <div>
                <h2 className="text-sm font-black text-slate-800 mb-1">Simulation Mode</h2>
                <p className="text-[11px] text-slate-500 leading-relaxed max-w-md">
                  The current sandbox environment (SAP API Business Hub) rejects
                  any write operation (POST/PUT) outright -- it returns an{' '}
                  <code className="bg-slate-100 px-1 rounded">OPERATION_NOT_SUPPORTED</code> error
                  even when the key and connection are fine. When you enable
                  Simulation Mode, material requests are recorded locally in the
                  database (and flagged as "Simulated" in the log) without us
                  attempting to reach SAP at all, so you can keep testing the
                  rest of the adapter. Turn this off once you have a real
                  connection (Production or a full trial environment) so
                  requests go back to being sent to SAP for real.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSimulationMode((s) => !s)}
              className={`shrink-0 w-12 h-7 rounded-full flex items-center px-1 transition-colors ${simulationMode ? 'bg-amber-500 justify-end' : 'bg-slate-300 justify-start'}`}
              aria-pressed={simulationMode}
            >
              <span className="w-5 h-5 bg-white rounded-full shadow-sm" />
            </button>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-6 py-2.5 rounded-lg flex items-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Save Settings
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;
